import Constants from '../Constants.js';

/**
 * This is meant to be a generic, reusable user action generator.
 * It needs the resourceId.
 * <code>
 * user : ( userId ) => {
 *   return new User( {
 *     userId,
 *     resourceId : this.id, // with is th device, system, project, script, etc id
 *     resourceApi : ( dispatch, getState, opts, resourceId ) => {
 *       // this return's a the api on which the user's calls exist
 *       return opts.api.resource( resourceId ) 
 *     }
 *   })
 * }
 */
class ResourceUsers {
  constructor({
    resourceId,
    resourceApi
  }) {
    this.resourceApi = resourceApi;
    this.resourceId = resourceId;
  }
  createAction(method, status, func) {
    return (dispatch, getState, opts) => {
      dispatch({
        type: Constants.RESOURCE_USER_STATUS,
        resourceId: this.resourceId,
        status
      });
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      // override top level api with resource api
      return func(dispatch, getState, {
        ...opts,
        api
      }, this.resourceId).catch(error => {
        dispatch({
          type: Constants.RESOURCE_USER_STATUS,
          resourceId: this.resourceId,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }
  list(opts) {
    return this.createAction('load', 'loading', async (dispatch, getState, {
      api
    }, resourceId) => {
      const {
        requestId,
        ...reqOpts
      } = opts || {};
      const r = await api.users().list(reqOpts);
      dispatch({
        type: Constants.RESOURCE_USER_LIST,
        requestId,
        resourceId,
        ...r
      });
      return r;
    });
  }
  canList() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      const r = await api.users().list({
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.resourceId,
        action: 'listUsers',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  add(opts) {
    return this.createAction('addUser', 'adding user', async (dispatch, getState, {
      api
    }, resourceId) => {
      const user = await api.users().add(opts);
      dispatch({
        type: Constants.RESOURCE_USER_ADD,
        resourceId,
        user
      });
      return user;
    });
  }

  /**
   * userId: string or [ string ] 
   */
  remove(opts) {
    return this.createAction('removeUsers', 'removing user', async (dispatch, getState, {
      api
    }, resourceId) => {
      const r = await api.users().remove(opts);
      const {
        userId
      } = opts;
      if (typeof userId === 'string') {
        dispatch({
          type: Constants.RESOURCE_USER_REMOVE,
          resourceId,
          userId
        });
      } else if (Array.isArray(userId)) {
        userId.forEach(u => {
          dispatch({
            type: Constants.RESOURCE_USER_REMOVE,
            resourceId,
            userId: u
          });
        });
      }
      return r;
    });
  }
  canAssignRoles() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      const r = await api.users().add({
        userId: ''
      }, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.resourceId,
        action: 'assignUserRoles',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  canCreateUser() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      const r = await api.users().add({}, {
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.resourceId,
        action: 'createUser',
        allowed: r.allowed === true
      });
      return r;
    };
  }
  canReadUsers() {
    return async (dispatch, getState, opts) => {
      const api = this.resourceApi(dispatch, getState, opts, this.resourceId);
      const r = await api.users().list({
        test: true
      });
      dispatch({
        type: Constants.ALLOWED_SET,
        resourceId: this.resourceId,
        action: 'readUsers',
        allowed: r.allowed === true
      });
      return r;
    };
  }
}
export default ResourceUsers;